
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import print from "print-js";
import moment from 'moment';
import swal from 'sweetalert';
import order from '../class/order';
import production from '../class/production';
import employee from '../class/employee';
import { formatRpcError } from '../class/jrpc20WSClient';
import FitText from './component/FitText';
import "../styles/OrderDatails.css";

const logedEmployee = new employee();

// Foglio pallet UDC: arriva come HTML dal webservice e si stampa da un iframe nascosto. HTML e non
// PDF perche' il browser prende il formato dal suo `@page { size: A4 }`, invece di riproporre la
// carta dell'ultima stampa (es. A3).
const printHtml = (html, done) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    iframe.onload = () => {
        let finished = false;
        const finish = () => {
            if (finished) return;
            finished = true;
            setTimeout(() => iframe.remove(), 1000);
            done();
        };
        iframe.contentWindow.addEventListener('afterprint', finish);
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // In Chrome print() resta in attesa finche' la finestra di stampa e' aperta
        finish();
    };
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
};

// Foglio pallet richiesto dal cliente: file caricato in anagrafica, stampato con print-js
const printPdf = (url, done) => {
    print({ printable: url, type: 'pdf', showModal: true, onPrintDialogClose: done });
};

// Stampe del pallet completato, una alla volta: prima i fogli UDC, poi l'eventuale foglio cliente
const printPalletSheets = (udcHtml, customerPdfUrl) => {
    const steps = [];
    if (udcHtml) steps.push((done) => printHtml(udcHtml, done));
    if (customerPdfUrl) steps.push((done) => printPdf(customerPdfUrl, done));
    const next = () => {
        const step = steps.shift();
        if (step) step(next);
    };
    next();
};

// Medie in confezioni/ora/persona, come "Media prevista (cfz/h/ps)" dell'offerta
const MEDIA_UNIT = 'cfz/h/ps';

// Valore in grande nella testata, con l'unita' di misura in piccolo sotto (accanto non ci sta a 1366px)
const OrderKpi = ({ label, value, unit }) => (
    <div className="order-kpi-item">
        <span className="order-kpi-label">{label}</span>
        <span className="order-kpi-value">{value}</span>
        {unit && <span className="order-kpi-unit">{unit}</span>}
    </div>
);

const OrderDetails = () => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [pallet, setPallet] = useState(0);
    const [quantity, setQuantity] = useState(0);
    const [lot, setLot] = useState('');
    const [loading, setLoading] = useState(false)
    const location = useLocation();
    const { order_uuid } = useParams();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);

    const openModal = () => setShowModal(true);
    const closeModal = () => setShowModal(false);

    // Pezzi registrati sul pallet aperto, non ancora su un foglio pallet (order::getInfo)
    const openPalletQuantity = () => Number(orderDetails?.open_pallet_quantity) || 0;

    const fetchData = async () => {
        setLoading(true);
        try {
            const orderInfo = new order();
            const orderInfoDetails = await orderInfo.getInfo({ order_uuid });
            console.log("orderInfoDetails: ", orderInfoDetails);
            setOrderDetails(orderInfoDetails);
            // Unità per pallet (pezzi) calcolata dal backend = colli/piano × piani/pallet × unità/collo.
            // I campi plan_packaging/pallet_plans non sono più sull'articolo (spostati in articlesIP).
            setPallet(orderInfoDetails.units_per_pallet ?? 0);
            setQuantity(orderInfoDetails.worked_quantity);
            setLot(orderInfoDetails.lot);
        } catch (error) {
            console.error("Error fetching the data", error);
            swal("Errore", formatRpcError(error), "error");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        fetchData();
    }, [pallet, quantity]);

    const handleAddPallet = async () => {
        // Come addPalletQuantity: si registrano solo i pezzi che mancano a chiudere il pallet aperto
        const newQuantity = parseFloat(quantity) + parseFloat(pallet) - openPalletQuantity();
        const orderedQuantity = orderDetails.quantity;

        if (newQuantity > orderedQuantity) {
            const confirm = await swal({
                title: "Attenzione!",
                text: "Aggiungendo un pallet si supera la quantità ordinata. Vuoi continuare?",
                icon: "info",
                buttons: ["Non aggiungere", "Sì, continua"],
                dangerMode: true,
            });

            if (!confirm) return;
        }

        swal({
            title: 'Si prega di attendere.',
            text: 'Aggiungiamo il numero di pallet',
            icon: 'info',
            button: false
        });

        try {
            const orderInfoDet = new production();
            const response = await orderInfoDet.addPalletQuantity({ order_uuid: orderDetails.uuid });
            console.log("RESPONSEEEEEE: ", response);

            if (response?.ok) {
                printPalletSheets(response.udc_print_html, response.print_url);
                swal({
                    title: "Success",
                    text: "Il Numero di pallet è stato aggiornato con successo." + (response.udc_error ? "\n" + response.udc_error : ""),
                    icon: response.udc_error ? "warning" : "success",
                    button: "OK"
                }).then(() => {
                    // scaricare o stampre il foglio pallet
                    fetchData();
                    logedEmployee.logout();
                    navigate('/login');
                });
            } else {
                console.error("Invalid response structure: ", response);
                swal("Error", "Il Numero di pallet non è stato aggiunto con successo.", "error");
            }
        } catch (error) {
            console.error("Error updating pallet quantity:", error);
            swal("Errore", formatRpcError(error), "error");
        }
    };

    const handleAddManualePallet = async () => {

        const inputQuantity = await swal({
            title: 'Inserisci quantità',
            text: 'Inserisci il numero di quantità manualmente:',
            content: {
                element: "input",
                attributes: {
                    placeholder: "Inserisci il numero",
                    type: "number",
                    min: "1",
                },
            },
            icon: 'info',
            buttons: true,
        });

        if (!inputQuantity) {
            swal("Error", "Non è stato inserito nessun valore.", "error");
            return;
        }

        const newQuantity = parseFloat(quantity) + parseFloat(inputQuantity);
        const orderedQuantity = parseFloat(orderDetails.quantity);

        // Saldo a zero o superato: e' l'ultimo pallet. La quantita' manuale non lo chiude (niente foglio
        // UDC se non si riempie un pallet), "Completa ordine" registra la stessa quantita', stampa il
        // foglio dell'ultimo pallet e mette l'ordine in Evaso.
        if (newQuantity >= orderedQuantity) {
            const over = newQuantity - orderedQuantity;
            const choice = await swal({
                title: "Saldo raggiunto",
                text: (over > 0
                    ? "Con questa quantità si supera la quantità ordinata di " + over + "."
                    : "Con questa quantità l'ordine arriva al saldo.")
                    + "\nVuoi completare l'ordine? Verrà messo in Evaso e verrà stampato il foglio pallet da "
                    + formatNumber(openPalletQuantity() + Number(inputQuantity), 0) + ".",
                icon: "warning",
                buttons: {
                    cancel: "Annulla",
                    manual: { text: "Solo quantità", value: "manual" },
                    complete: { text: "Completa ordine", value: "complete" },
                },
            });

            if (choice === "complete") {
                await completeOrder({ quantity: Number(inputQuantity) });
                return;
            }
            if (choice !== "manual") return;
        }

        await registerManualQuantity(Number(inputQuantity));
    };

    // Quantita' manuale: avanzamento ed eventuali fogli UDC dei pallet che si chiudono, stampati subito
    const registerManualQuantity = async (manualQuantity) => {
        try {
            const orderInfoDet = new production();
            const response = await orderInfoDet.addManualQuantity({
                order_uuid: orderDetails.uuid,
                quantity: manualQuantity
            });

            if (response?.ok) {
                printPalletSheets(response.udc_print_html, response.print_url);
                swal("Success",
                    "La quantità è stata aggiornata con successo." + (response.udc_error ? "\n" + response.udc_error : ""),
                    response.udc_error ? "warning" : "success")
                    .then(() => {
                        fetchData();
                        logedEmployee.logout();
                        navigate('/login');
                    });
            } else {
                console.error("Invalid response structure: ", response);
                swal("Error", "La quantità non è stata aggiornata con successo.", "error");
            }
        } catch (error) {
            console.error("Error updating the quantity:", error);
            swal("Errore", formatRpcError(error), "error");
        }
    };

    // Completamento ordine: UN foglio UDC per l'ultimo pallet, ordine in Evaso. `quantities` e'
    // { pallet_quantity } (q.ta' del pallet finale, dal bottone) o { quantity } (pezzi prodotti ora, da "Saldo raggiunto")
    const completeOrder = async (quantities) => {
        try {
            const response = await new production().completeOrder({
                order_uuid: orderDetails.uuid,
                ...quantities
            });

            if (response?.ok) {
                printPalletSheets(response.udc_print_html, response.print_url);
                swal("Success",
                    "L'ordine è stato completato e messo in Evaso." + (response.udc_error ? "\n" + response.udc_error : ""),
                    response.udc_error ? "warning" : "success")
                    .then(() => {
                        logedEmployee.logout();
                        navigate('/login');
                    });
            } else {
                console.error("Invalid response structure: ", response);
                swal("Error", "L'ordine non è stato completato.", "error");
            }
        } catch (error) {
            console.error("Error completing the order:", error);
            swal("Errore", formatRpcError(error), "error");
        }
    };

    const handleCompleteOrder = async () => {
        // Input creato a mano: con content "input" lo swal restituisce "" se il valore proposto non viene toccato
        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.className = 'swal-content__input';

        // Si indica la quantita' del pallet finale, quella del foglio (proposta: pallet aperto + saldo).
        // Come avanzamento si registra solo la differenza con i pezzi gia' sul pallet, aggiornata mentre si digita.
        const open = openPalletQuantity();
        input.value = String(open + Math.max(Number(orderDetails.remain_quantity) || 0, 0));
        const sheetInfo = document.createElement('p');
        sheetInfo.className = 'complete-order-sheet';
        const refreshSheetInfo = () => {
            const palletQuantity = Math.max(Number(input.value) || 0, 0);
            sheetInfo.textContent = open > 0
                ? "Sul pallet ci sono già " + formatNumber(open, 0) + " pz.\nAvanzamento da registrare: "
                    + formatNumber(palletQuantity, 0) + " − " + formatNumber(open, 0) + " = " + formatNumber(Math.max(palletQuantity - open, 0), 0)
                : "";
        };
        input.addEventListener('input', refreshSheetInfo);
        refreshSheetInfo();

        const content = document.createElement('div');
        content.append(input, sheetInfo);

        const confirm = await swal({
            title: 'Completa ordine',
            text: "Quantità del pallet finale, da stampare sul foglio pallet.\nL'ordine verrà messo in Evaso.",
            content,
            icon: 'warning',
            buttons: ["Annulla", "Completa ordine"],
        });

        if (!confirm) return;

        const palletQuantity = Number(input.value);
        if (input.value === '' || isNaN(palletQuantity) || palletQuantity < 0) {
            swal("Error", "Inserire una quantità valida.", "error");
            return;
        }
        if (palletQuantity < open) {
            swal("Error", "Sul pallet ci sono già " + formatNumber(open, 0) + " pz: la quantità del pallet non può essere inferiore.", "error");
            return;
        }

        await completeOrder({ pallet_quantity: palletQuantity });
    };


    // Render loading state
    if (loading) {
        return <div>Loading order details...</div>;
    }

    // Render if no order details are found
    if (!orderDetails) {
        return <div>No order details available.</div>;
    }

    const goBackToOrders = () => {
        navigate('/choose_order');
    };
    const formatNumber = (value, decimals = 5) => {
        if (value == null || isNaN(value)) return (0).toFixed(decimals).replace('.', ',');

        return Number(value).toLocaleString('it-IT', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    };
    // Medie come nel report Produttivita' (2 decimali), "-" quando il backend non le sa calcolare
    const formatMedia = (value) => value == null ? "-" : formatNumber(value, 2);
    // Convert Unix timestamp to formatted date
    const formatDate = (timestamp) => {
        const n = Number(timestamp);
        if (!timestamp || isNaN(n) || n === 0) return "-";
        return moment.unix(n).format('DD-MM-YYYY');
    };

    // Saldo e medie in grande nella testata, accanto al logo (slot dell'Header)
    const headerSlot = document.getElementById('header-slot');

    return (
        <div className='bloc-details'>
            {headerSlot && createPortal(
                <div className="order-kpi">
                    <OrderKpi label="Quantità saldo" value={formatNumber(orderDetails.remain_quantity)} unit={orderDetails.offer?.unit_of_measure} />
                    <OrderKpi label="Media prevista" value={formatMedia(orderDetails.media_reale)} unit={orderDetails.media_reale == null ? null : MEDIA_UNIT} />
                    <OrderKpi label="Media effettiva" value={formatMedia(orderDetails.media_effettiva)} unit={orderDetails.media_effettiva == null ? null : MEDIA_UNIT} />
                </div>,
                headerSlot
            )}
            <div className="order-block-text">
                {/*  <h2 className='order-page-title'>Pagina scelta ordine</h2> */}
                <table>
                    <thead>
                        <tr className='table-head' style={{ backgroundColor: '#003049', color: '#fff', fontWeight: 'bold' }}>
                            <th>N. Ordine</th>
                            <th>Cod. art. LAS</th>
                            <th>Cod. art. cliente</th>
                            <th>Tipo Pallet</th>
                            <th>Dettagli</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className='order-detail'>
                            <td>{orderDetails.order_production_number}</td>
                            <td>{orderDetails.article?.cod_article_las ?? "ARTICOLO NON TROVATO"}</td>
                            <td>{orderDetails.article?.cod_article_client || "-"}</td>
                            <td>{orderDetails.pallet_type ? `${orderDetails.pallet_type.cod} - ${orderDetails.pallet_type.description}` : "-"}</td>
                            <td> <button onClick={openModal} className="blue_butt ">Dettagli dell'ordine</button></td>
                        </tr>
                    </tbody>
                </table>

                {showModal && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <h2 className='modal-title'>Dettagli dell'ordine:</h2>
                            <p className='order-details' ><span className='order-details-name'>Numero Ordine Produzione:</span> <span className='order-details-value' >{orderDetails.order_production_number}</span> </p>
                            <p className='order-details' ><span className='order-details-name'>Descrizione Articolo:</span> <span className='order-details-value'>{orderDetails.article ? `${orderDetails.article.cod_article_las} - ${orderDetails.article.article_descr ?? ""}` : "ARTICOLO NON TROVATO"}</span></p>
                            <p className='order-details' ><span className='order-details-name'>Quantità Ordinata:</span> {formatNumber(orderDetails.quantity)}</p>
                            <p className='order-details' ><span className='order-details-name'>Quantità lavorata:</span> {formatNumber(quantity)}</p>
                            <p className='order-details' ><span className='order-details-name'>Quantità saldo:</span> <b>{formatNumber(orderDetails.remain_quantity)}</b></p>
                            <p className='order-details' ><span className='order-details-name'>Quantità pallet:</span> {formatNumber(pallet)}</p>
                            <p className='order-details' ><span className='order-details-name'>Lotto:</span> {lot}</p>
                            <p className='order-details' ><span className='order-details-name'>Data di scadenza:</span> {orderDetails.expiration_date_formatted || formatDate(orderDetails.expiration_date)}</p>
                            <p className='order-details' ><span className='order-details-name'>Linea di Lavoro :</span> {orderDetails.lasworkline?.name ?? "Linea di lavoro non trovata"}</p>
                            <button onClick={closeModal} className="blue_butt">Chiudi</button>
                        </div>
                    </div>
                )}

                {/*  {orderStarted === "1" ? (
                <button className='blue_butt' onClick={handleStopOrder}>Stop Order</button>
            ) : (
                <button className='blue_butt' onClick={handleStartServer}>Start Order</button>
            )} */}
            </div>
            <div className="order-block-btn">
                <button onClick={handleAddPallet} className='btn-aggiungi-pallet-success' ><FitText>Completa pallet</FitText></button>
                <button onClick={handleAddManualePallet} className='btn-manuale'><FitText>Quantità manuale</FitText></button>
                <button onClick={handleCompleteOrder} className='btn-completa-ordine'><FitText>Completa ordine</FitText></button>
            </div>
        </div>
    );
};

export default OrderDetails;
